// Client for the parse-commercial-invoice Edge Function (Gemini PDF text extraction runs
// server-side; the API key never reaches the browser).
import { supabase } from './supabase'

export type ParserProvider = 'gemini' | 'anthropic'

export interface PdfExtractResult {
  rawText: string
  confidence: number
  // Diagnostics the server adds; normal callers only need rawText/confidence.
  provider?: ParserProvider
  model?: string
  durationMs?: number
  // Present when the default provider failed and the other one answered instead.
  fallback?: { from: ParserProvider; code: string; message: string }
  usage?: { inputTokens: number | null; outputTokens: number | null }
}

export type PdfExtractErrorCode = 'NOT_CONFIGURED' | 'KEY_INVALID' | 'PERMISSION_DENIED' | 'MODEL_UNAVAILABLE'
  | 'RATE_LIMITED' | 'OVERLOADED' | 'TIMEOUT' | 'BAD_PDF' | 'UPSTREAM_ERROR' | 'UNKNOWN'

const USER_MESSAGES: Record<PdfExtractErrorCode, string> = {
  NOT_CONFIGURED: 'the AI provider key is not set up on the server',
  KEY_INVALID: 'the AI provider rejected our API key (invalid or expired)',
  PERMISSION_DENIED: 'the AI provider denied permission (403) -- the API key or its restrictions need checking',
  MODEL_UNAVAILABLE: 'the configured AI model is no longer available',
  RATE_LIMITED: 'the AI provider is rate-limiting us -- wait a minute and try again',
  OVERLOADED: 'the AI provider is overloaded right now -- try again in a minute',
  TIMEOUT: 'the AI provider took too long to respond',
  BAD_PDF: 'the PDF could not be read (it may be corrupted, password-protected or too large)',
  UPSTREAM_ERROR: 'the AI provider returned an error',
  UNKNOWN: 'an unexpected error occurred',
}

// `message` stays technical (shown on the Admin compare page and in the console); `userMessage` is
// the plain-English reason for staff-facing banners.
export class PdfExtractError extends Error {
  code: PdfExtractErrorCode
  userMessage: string
  constructor(code: PdfExtractErrorCode, technical: string) {
    super(technical)
    this.code = code
    this.userMessage = USER_MESSAGES[code]
  }
}

// `provider` overrides the default from company_settings -- ADMIN only (server returns 403 otherwise).
export async function extractTextFromPDF(pdfBase64: string, provider?: ParserProvider): Promise<PdfExtractResult> {
  const { data, error } = await supabase.functions.invoke('parse-commercial-invoice', {
    body: { pdfBase64, provider },
  })
  if (error) {
    // FunctionsHttpError carries the response; surface the server's message when there is one.
    const detail = await (error as { context?: Response }).context?.json?.().catch(() => null)
    throw new PdfExtractError((detail?.code as PdfExtractErrorCode) ?? 'UNKNOWN', detail?.error ?? error.message)
  }
  const result = data as PdfExtractResult
  if (result.fallback) {
    console.warn(`[parse-commercial-invoice] ${result.fallback.from} failed (${result.fallback.code}); used ${result.provider} instead: ${result.fallback.message}`)
  }
  return result
}

export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      resolve(result.split(',')[1])
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
